import { memo } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { LinkIcon } from '@air/design-icons';

import { ReadonlyShareLink } from '~/components/ShareModal/components/ShareLink/ReadonlyShareLink';
import { AssetVersionShareLinkSelector } from '~/components/SharePrivateClipModal/AssetVersionShareLinkSelector';
import {
  type AssetShareLinkOption,
  getAssetShareLinkOptionSelection,
  isAssetShareLinkEnabled,
} from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';

export type ReadonlyAssetSharePrivateLinkProps = {
  isFetchingShareLinkOptions?: boolean;
  onSelectedOptionChange: (optionKey: string) => void;
  options: AssetShareLinkOption[];
  selectedOption: AssetShareLinkOption | undefined;
  trackLocation: AirActionTrackingLocation;
};

export const ReadonlyAssetSharePrivateLink = memo(
  ({
    isFetchingShareLinkOptions = false,
    onSelectedOptionChange,
    options,
    selectedOption,
    trackLocation,
  }: ReadonlyAssetSharePrivateLinkProps) => {
    const { selectedOptionKey, selectedSlotShortUrl } = getAssetShareLinkOptionSelection(selectedOption);
    const hasShareableLink = isAssetShareLinkEnabled(selectedSlotShortUrl);

    const renderShareLink = () => {
      if (!hasShareableLink || !selectedSlotShortUrl) {
        return (
          <div className="flex items-center">
            <LinkIcon className="ads:text-icon-subtle text-gray-9" size="sm" />
            {/* biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration */}
            <p className="pl-2 ads:text-metadata text-16 text-gray-9" data-testid={'NO_SHARE_LINK'}>
              No shareable link
            </p>
          </div>
        );
      }

      return (
        <ReadonlyShareLink
          objectId={selectedSlotShortUrl.objectId}
          objectType={selectedSlotShortUrl.objectType}
          shareId={selectedSlotShortUrl.id}
          shortIdInfo={selectedSlotShortUrl}
          trackLocation={trackLocation}
        />
      );
    };

    return (
      <div className="flex flex-col gap-2" key={selectedOptionKey}>
        <AssetVersionShareLinkSelector
          hideInactiveOptions
          isFetchingShareLinkOptions={isFetchingShareLinkOptions}
          onSelect={onSelectedOptionChange}
          options={options}
          selectedOptionKey={selectedOptionKey}
        />
        {renderShareLink()}
      </div>
    );
  },
);

ReadonlyAssetSharePrivateLink.displayName = 'ReadonlyAssetSharePrivateLink';
